import { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'

import { FaqSearchSection } from 'sections/faqs'
import { Faq as FaqModel, strapi } from 'api'
import { BackendError } from 'api/strapi/BackendError'
import { REVALIDATION_ERROR_TIMEOUT, REVALIDATION_TIMEOUT } from 'const/config'
import { Markdown } from 'components/markdown'
import { Heading } from 'components/heading'
import { LinkButton } from 'components/links'
import { routes } from 'utils/routes'
import { CTASection } from 'sections/common'
import { FaqLink } from 'components/faqs'
import { cn } from 'utils'
import { OffsetSection } from 'sections/faqs/offset-section'

import classes from './faq.module.css'

interface FaqProps {
  faq: FaqModel
}

export default function Faq({ faq }: FaqProps) {
  const hasRelatedFaq = !!faq.related_faqs?.length
  const faqCategory = faq.faq_category

  return (
    <>
      <NextSeo title={faq.title} />

      <FaqSearchSection />
      <OffsetSection className={classes.section} classNameInnerContainer={classes.container}>
        <header className={classes.header}>
          <Heading theme="h5" component="h2" className={classes.title}>
            {faq.title}
          </Heading>

          <LinkButton
            theme="goBack"
            href={faqCategory ? routes.faqCategory(faqCategory.slug) : routes.faqs}
            className={classes.backButton}
          >
            Back To {faqCategory ? faqCategory.title : 'All topics'}
          </LinkButton>
        </header>

        <Markdown className={cn(classes.markdown, hasRelatedFaq && classes.withRelatedFaq)}>
          {faq.content}
        </Markdown>

        {hasRelatedFaq && (
          <>
            <Heading theme="h5" component="h2" className={classes.title}>
              Related topics
            </Heading>

            <ul className={classes.relatedList}>
              {faq.related_faqs?.map((faq) => (
                <li key={faq.id}>
                  <FaqLink {...faq} />
                </li>
              ))}
            </ul>
          </>
        )}
      </OffsetSection>

      <CTASection />
    </>
  )
}

export const getStaticProps: GetStaticProps<FaqProps> = async (context) => {
  const { slug } = context.params ?? {}

  if (!slug || Array.isArray(slug)) {
    return {
      notFound: true,
      revalidate: REVALIDATION_ERROR_TIMEOUT,
    }
  }

  try {
    const faqData = await strapi.faqs.find({
      filters: {
        slug: { $eq: slug },
      },
      populate: ['related_faqs', 'faq_category'],
    })

    const faq = faqData?.items?.[0]

    if (!faq) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }

    return {
      revalidate: REVALIDATION_TIMEOUT,
      props: {
        faq,
      },
    }
  } catch (error) {
    if ((error as BackendError).notFound) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }
    throw error
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await strapi.faqs.listSlugs()
  return {
    fallback: 'blocking',
    paths: slugs.map((slug) => ({
      params: {
        slug: String(slug),
      },
    })),
  }
}
